import React, { useEffect, useState } from 'react';
import { REVIEW_REPLY_MAX_LENGTH } from '../hooks/useStoreReviews';

const ReplyEditor = ({ review, storeName, onSave, isSaving = false, icon = null }) => {
  const existingReply = review?.merchantReply || '';
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(existingReply);
  const [error, setError] = useState('');

  useEffect(() => {
    setDraft(existingReply);
    setIsEditing(false);
    setError('');
  }, [existingReply]);

  const trimmedDraft = draft.trim();
  const isTooLong = trimmedDraft.length > REVIEW_REPLY_MAX_LENGTH;
  const canSave = trimmedDraft.length > 0 && !isTooLong && !isSaving;

  const startEditing = () => {
    setDraft(existingReply);
    setIsEditing(true);
    setError('');
  };

  const cancelEditing = () => {
    setDraft(existingReply);
    setIsEditing(false);
    setError('');
  };

  const submitReply = async () => {
    if (!trimmedDraft) {
      setError('Reply cannot be blank.');
      return;
    }
    if (isTooLong) {
      setError(`Reply must be ${REVIEW_REPLY_MAX_LENGTH} characters or fewer.`);
      return;
    }

    setError('');
    try {
      await onSave?.(review.reviewUuid, trimmedDraft);
      setIsEditing(false);
    } catch (saveError) {
      setError(saveError?.message || 'Could not save reply.');
    }
  };

  return (
    <div className="review-card__reply-wrap">
      {existingReply && !isEditing && (
        <div className="review-card__reply">
          <div className="review-card__reply-head">
            <span className="review-card__reply-label">Response from {storeName || 'store'}</span>
            <button
              type="button"
              className="review-card__reply-edit"
              onClick={startEditing}
            >
              Edit
            </button>
          </div>
          <p className="review-card__reply-text">{existingReply}</p>
        </div>
      )}

      {isEditing && (
        <div className="review-card__reply-editor">
          <label className="review-card__reply-label" htmlFor={`reply-${review.reviewUuid}`}>
            Response from {storeName || 'store'}
          </label>
          <textarea
            id={`reply-${review.reviewUuid}`}
            className="review-card__reply-input"
            value={draft}
            maxLength={REVIEW_REPLY_MAX_LENGTH + 1}
            onChange={(event) => {
              setDraft(event.target.value);
              setError('');
            }}
            placeholder="Write a concise response to this customer review"
            aria-describedby={`reply-count-${review.reviewUuid}`}
          />
          <div className="review-card__reply-editor-meta">
            <span
              id={`reply-count-${review.reviewUuid}`}
              className={isTooLong ? 'review-card__reply-count review-card__reply-count--error' : 'review-card__reply-count'}
            >
              {trimmedDraft.length}/{REVIEW_REPLY_MAX_LENGTH}
            </span>
            {error && <span className="review-card__reply-error">{error}</span>}
          </div>
          <div className="review-card__reply-actions">
            <button
              type="button"
              className="review-card__reply-cancel"
              disabled={isSaving}
              onClick={cancelEditing}
            >
              Cancel
            </button>
            <button
              type="button"
              className="review-card__reply-save"
              disabled={!canSave}
              onClick={submitReply}
            >
              {isSaving ? 'Saving...' : 'Save reply'}
            </button>
          </div>
        </div>
      )}

      {!existingReply && !isEditing && (
        <button
          type="button"
          className="review-card__reply-add"
          onClick={startEditing}
        >
          {icon}
          Reply
        </button>
      )}
    </div>
  );
};

export default ReplyEditor;
